import { afterNextRender, Directive, ElementRef, inject, Input, OnDestroy } from '@angular/core';

/**
 * Zählt beim Sichtbarwerden von 0 auf den Zielwert hoch (nur im Browser).
 * Nutzung: <span [mbCountUp]="1909" suffix=" mm"></span>
 * Bei SSR/ohne JS steht direkt der Zielwert im Element (Template-Fallback).
 */
@Directive({
  selector: '[mbCountUp]',
  standalone: true,
})
export class CountUpDirective implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private observer?: IntersectionObserver;
  private raf = 0;

  @Input('mbCountUp') target = 0;
  @Input() suffix = '';
  @Input() duration = 1100;

  constructor() {
    afterNextRender(() => {
      const el = this.host.nativeElement as HTMLElement;
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this.observer?.unobserve(entry.target);
            this.animate(el);
          }
        },
        { threshold: 0.4 }
      );
      this.observer.observe(el);
    });
  }

  private format(n: number): string {
    return Math.round(n).toLocaleString('de-DE') + this.suffix;
  }

  private animate(el: HTMLElement) {
    const start = performance.now();
    const from = 0;
    const to = this.target;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / this.duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = this.format(from + (to - from) * eased);
      if (t < 1) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }
}
